import React, { useMemo, useState } from 'react';
import { styled } from '../../../../../shared/Theme/theme';
import { bpAccountingRow } from '../../../../../../models/entities/businessPartner/accounting';
import Button from '../../../../../shared/SmallComponents/Button';
import { DropDown, TextInput, TextArea, DatePicker } from '../../../../../shared/inputs/base';
import NumberInput from '../../../../../shared/inputs/base/NumberInput';
import { currencyOptions } from '../../../../../shared/inputs/form/CurrencyInput/CurrencyInput';

type Props = {
    rows: bpAccountingRow[];
    defaultShipmentId?: string;
    onClose: () => void;
    onSubmit: (input: {
        shipmentId: string;
        amount: number;
        currency: string;
        paidAt: string;
        reference?: string;
        method?: string;
        comment?: string;
    }) => Promise<void>;
};

const METHOD_OPTIONS = [
    { value: 'BANK_TRANSFER', text: 'Bank transfer' },
    { value: 'CHECK', text: 'Check' },
    { value: 'CASH', text: 'Cash' },
    { value: 'CREDIT_CARD', text: 'Credit card' },
    { value: 'OTHER', text: 'Other' }
];

const AddPaymentModal = ({ rows, defaultShipmentId, onClose, onSubmit }: Props) => {
    const initialShipment = rows.find((r) => r.shipmentId === defaultShipmentId) ?? rows[0];
    const [shipmentId, setShipmentId] = useState<string>(initialShipment?.shipmentId ?? '');
    const [amount, setAmount] = useState<number>(initialShipment ? Number(initialShipment.openBalance.toFixed(2)) : 0);
    const [currency, setCurrency] = useState<string>(initialShipment?.currency ?? 'USD');
    const [paidAt, setPaidAt] = useState<Date>(new Date());
    const [method, setMethod] = useState<string | undefined>(undefined);
    const [reference, setReference] = useState<string>('');
    const [comment, setComment] = useState<string>('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const shipmentOptions = useMemo(
        () =>
            rows.map((r) => ({
                value: r.shipmentId,
                text: `${r.shipmentNumber} — open ${r.openBalance.toFixed(2)} ${r.currency}`
            })),
        [rows]
    );

    const onShipmentChange = (id: string) => {
        setShipmentId(id);
        const r = rows.find((row) => row.shipmentId === id);
        if (r) {
            setAmount(Number(r.openBalance.toFixed(2)));
            setCurrency(r.currency);
        }
    };

    const handleSubmit = async () => {
        if (!shipmentId) return setError('Please select a shipment');
        if (!Number.isFinite(amount) || amount <= 0) return setError('Amount must be a positive number');
        if (!currency) return setError('Currency required');
        setSubmitting(true);
        setError(null);
        try {
            await onSubmit({
                shipmentId,
                amount,
                currency,
                paidAt: paidAt.toISOString().slice(0, 10),
                method: method || undefined,
                reference: reference.trim() || undefined,
                comment: comment.trim() || undefined
            });
            onClose();
        } catch (e) {
            setError((e as Error)?.message || 'Failed to record payment');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Wrapper>
            <Field>
                <Label>Shipment</Label>
                <DropDown
                    options={shipmentOptions}
                    value={shipmentId}
                    onChange={onShipmentChange}
                    showSearch
                    placeHolder="Select a shipment"
                    width="100%"
                />
            </Field>
            <Row>
                <Field $flex={2}>
                    <Label>Amount</Label>
                    <NumberInput
                        value={amount}
                        min={0}
                        step={1}
                        onChange={(v: number) => setAmount(Number(v) || 0)}
                        width="100%"
                    />
                </Field>
                <Field>
                    <Label>Currency</Label>
                    <DropDown
                        options={currencyOptions}
                        value={currency}
                        onChange={(v: string) => setCurrency(v)}
                        showSearch
                        width="100%"
                    />
                </Field>
            </Row>
            <Row>
                <Field>
                    <Label>Paid On</Label>
                    <DatePicker
                        value={paidAt}
                        onChange={(v: { toDate?: () => Date } | null) => {
                            if (v && typeof v.toDate === 'function') setPaidAt(v.toDate());
                        }}
                        width="100%"
                    />
                </Field>
                <Field>
                    <Label>Method</Label>
                    <DropDown
                        options={METHOD_OPTIONS}
                        value={method}
                        onChange={(v: string) => setMethod(v)}
                        allowClear
                        placeHolder="Select method"
                        width="100%"
                    />
                </Field>
            </Row>
            <Field>
                <Label>Reference</Label>
                <TextInput
                    value={reference}
                    onChange={(v: string) => setReference(v)}
                    placeHolder="Invoice / receipt number"
                />
            </Field>
            <Field>
                <Label>Comment</Label>
                <TextArea
                    value={comment}
                    onChange={(v: string) => setComment(v)}
                    placeHolder="Optional note about the payment"
                    autoSize={{ minRows: 2, maxRows: 4 }}
                />
            </Field>
            {error && <ErrorMsg>{error}</ErrorMsg>}
            <Actions>
                <Button onClick={onClose}>Cancel</Button>
                <Button buttonType="filled" onClick={handleSubmit} disabled={submitting}>
                    {submitting ? 'Saving…' : 'Save Payment'}
                </Button>
            </Actions>
        </Wrapper>
    );
};

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 4px 4px 8px;
    width: 420px;
`;

const Row = styled.div`
    display: flex;
    gap: 12px;
`;

const Field = styled.div<{ $flex?: number }>`
    display: flex;
    flex-direction: column;
    flex: ${(p) => p.$flex ?? 1};
    min-width: 0;
`;

const Label = styled.label`
    font-size: 12px;
    color: #4b5563;
    margin-bottom: 4px;
    font-weight: 500;
`;

const Actions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 4px;
`;

const ErrorMsg = styled.div`
    color: #b32424;
    font-size: 12px;
`;

export default AddPaymentModal;
